/**
 * AdMob service layer.
 *
 * Web (development / browser preview):
 *   - `isNative()` returns false.
 *   - `showInterstitial()` / `showRewarded()` resolve immediately with
 *     `{ mocked: true, rewarded: true }` so the existing mock-ad UI keeps
 *     working for local iteration.
 *
 * Native (Capacitor Android / iOS builds):
 *   - Uses @capacitor-community/admob.
 *   - Reads ad unit IDs from env (see /app/frontend/.env):
 *       REACT_APP_ADMOB_BANNER_ANDROID / _IOS
 *       REACT_APP_ADMOB_INTERSTITIAL_ANDROID / _IOS
 *       REACT_APP_ADMOB_REWARDED_ANDROID / _IOS
 *   - Automatically picks the right ID per platform.
 *
 * Usage:
 *     import { showRewarded, showInterstitial, showBanner } from "@/lib/admob";
 *     const { rewarded } = await showRewarded();
 *     if (rewarded) grantReward();
 */
import { Capacitor } from "@capacitor/core";
// Static (not lazy) import — splitting AdMob into its own webpack chunk
// caused ChunkLoadError on the live-wrapper AAB whenever the preview was
// redeployed (chunk hashes changed → the old AAB's reference to the OLD
// chunk filename 404'd → server returned HTML → "Unexpected token '<'").
// Static import bakes the module into bundle.js so the AAB doesn't depend
// on per-deploy chunk filenames. The web tree-shake will drop the native
// methods that are never called in browser mode.
import {
  AdMob as AdMobPlugin,
  BannerAdPosition as BannerAdPositionEnum,
  BannerAdSize as BannerAdSizeEnum,
  RewardAdPluginEvents,
  AdmobConsentStatus,
  AdmobConsentDebugGeography,
} from "@capacitor-community/admob";

// --- Ad diagnostics -------------------------------------------------------
// Iter92 — the rewarded-ad reward bug is fixed (confirmed on device), so the
// on-screen diagnostic toasts are DISABLED. We keep the adDiag() call sites
// (they now log to the console only) so we can flip visual debugging back on
// instantly if ever needed — but players/owner never see toasts again.
export function isAdDebug() {
  return false;
}
export function adDiag(msg) {
  // eslint-disable-next-line no-console
  try { console.log("[AD-DIAG]", msg); } catch { /* ignore */ }
}

let AdMob = AdMobPlugin;
let BannerAdPosition = BannerAdPositionEnum;
let BannerAdSize = BannerAdSizeEnum;

const env = process.env;

// Google's official test IDs — always safe to use before Google approves yours.
const TEST_IDS = {
  banner: {
    android: "ca-app-pub-3940256099942544/6300978111",
    ios: "ca-app-pub-3940256099942544/2934735716",
  },
  interstitial: {
    android: "ca-app-pub-3940256099942544/1033173712",
    ios: "ca-app-pub-3940256099942544/4411468910",
  },
  rewarded: {
    android: "ca-app-pub-3940256099942544/5224354917",
    ios: "ca-app-pub-3940256099942544/1712485313",
  },
};

export function isNative() {
  try { return Capacitor?.isNativePlatform?.() ?? false; } catch { return false; }
}

function platform() {
  try { return Capacitor?.getPlatform?.() ?? "web"; } catch { return "web"; }
}

function pickId(kind) {
  const p = platform();
  // Test-mode flag: when REACT_APP_ADMOB_USE_TEST_IDS=1 in frontend/.env we
  // ALWAYS use Google's universal test IDs which guarantee fill within
  // seconds. This is critical during the first 24-48h of Internal Testing
  // when AdMob hasn't verified the app yet and real ad units return
  // "Network error" or "No fill". Flip to 0 once the app is in production
  // and your AdMob dashboard shows real impressions.
  if (env.REACT_APP_ADMOB_USE_TEST_IDS === "1") {
    return TEST_IDS[kind][p === "ios" ? "ios" : "android"];
  }
  const envKey = `REACT_APP_ADMOB_${kind.toUpperCase()}_${p === "ios" ? "IOS" : "ANDROID"}`;
  return env[envKey] || TEST_IDS[kind][p === "ios" ? "ios" : "android"];
}

let initialised = false;
// Tracks the ad unit id that's currently cached/prepped by AdMob.
// Set by preloadRewarded() ahead of time, consumed by showRewarded().
let preloadedAdId = null;
async function ensureInit() {
  if (!isNative()) return false;
  if (initialised) return true;
  try {
    // Register the developer's own device(s) as AdMob TEST devices so they
    // see test ads (safe to tap) while REAL players get REAL revenue ads.
    // Set REACT_APP_ADMOB_TEST_DEVICE_ID to your device's AdMob test id
    // (printed in logcat: "Use RequestConfiguration.Builder.setTestDeviceIds
    // (Arrays.asList(\"XXXX\"))"). Comma-separate multiple ids. Leave empty
    // in production so everyone gets real ads.
    const testDevices = (env.REACT_APP_ADMOB_TEST_DEVICE_ID || "")
      .split(",").map((s) => s.trim()).filter(Boolean);
    await AdMob.initialize({
      requestTrackingAuthorization: true,
      testingDevices: testDevices,
      // Only enable test mode when we actually have a dev device listed;
      // otherwise real users would get test (non-paying) ads.
      initializeForTesting: testDevices.length > 0,
    });

    // ── UMP consent (GDPR / EEA / UK) ──────────────────────────────────
    // Standard Google-recommended flow: request the latest consent info,
    // then show the consent form ONLY when it is REQUIRED (EEA/UK users).
    // Runs once, right after initialize() and BEFORE any ad is requested
    // (all show*/preload* paths call ensureInit() first, so this gates
    // every ad request). Non-fatal: if UMP errors we still let the app
    // continue rather than blocking gameplay behind a consent hiccup.
    //
    // To verify the form on your OWN device set
    // REACT_APP_ADMOB_UMP_DEBUG_EEA=1 in frontend/.env — this forces the
    // SDK to treat the device as EEA even outside Europe. Leave it unset
    // (or 0) in production so real geography decides.
    try {
      const debugEEA = env.REACT_APP_ADMOB_UMP_DEBUG_EEA === "1";
      const consentReq = debugEEA
        ? { debugGeography: AdmobConsentDebugGeography.EEA, testDeviceIdentifiers: testDevices }
        : undefined;
      const info = await AdMob.requestConsentInfo(consentReq);
      adDiag(`UMP consent status=${info?.status} formAvailable=${info?.isConsentFormAvailable}`);
      if (info?.isConsentFormAvailable && info?.status === AdmobConsentStatus.REQUIRED) {
        const res = await AdMob.showConsentForm();
        adDiag(`UMP consent form dismissed → status=${res?.status}`);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("UMP consent flow failed (continuing without gate)", e);
    }

    initialised = true;
    return true;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("AdMob init failed — falling back to mock", e);
    return false;
  }
}

export async function showInterstitial() {
  const ok = await ensureInit();
  if (!ok) return { mocked: true, shown: true };
  try {
    await AdMob.prepareInterstitial({ adId: pickId("interstitial") });
    await AdMob.showInterstitial();
    return { mocked: false, shown: true };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("interstitial show failed", e);
    return { mocked: false, shown: false, error: String(e) };
  }
}

export async function showRewarded({ userId, rewardType, onReward, onShowing } = {}) {
  adDiag(`showRewarded() called (type=${rewardType})`);
  const ok = await ensureInit();
  if (!ok) { adDiag("✗ ensureInit FALSE → returning mock (no real ad, NO reward)"); return { mocked: true, rewarded: true }; }

  // ── Event-driven reward grant ───────────────────────────────────────
  // CRITICAL: per the official @capacitor-community/admob v7 docs, the
  // PROMISE returned by `showRewardVideoAd()` is NOT a reliable signal
  // for granting the reward. It often resolves with `undefined` even
  // when the user fully watched the ad. The plugin's intended source of
  // truth is the `RewardAdPluginEvents.Rewarded` listener.
  //
  // We register a listener BEFORE calling show, race it against the
  // Dismissed / FailedToLoad / FailedToShow events, and always clean up
  // regardless of outcome.
  //
  // Tester complaints v43 (without this fix):
  //   • "ad plays in full but no coach tip granted"  (Rewarded fired
  //     but the promise resolved before / instead of `reward.amount > 0`)
  //   • "ad opens for a sec and closes — no tiles highlighted"
  //     (Dismissed fired without Rewarded — user got the reward path
  //     anyway because the old code mistook the truthy `reward` for a
  //     full watch)
  //   • "ad couldn't load — try in a moment"  (FailedToLoad event
  //     never surfaced because we only inspected the prepare promise's
  //     error string).
  const showWithListeners = async (adId, onImmediateReward, onImmediateShowing) => {
    const subs = [];
    let rewarded = false;
    let dismissed = false;
    let errorMsg = null;
    let shownAt = 0;
    let rewardDispatched = false;
    let showingDispatched = false;
    let loadGuard = null;   // fires only while the ad is still LOADING
    let watchGuard = null;  // safety net once the ad is on screen
    let resolveSettled;
    const settled = new Promise((resolve) => { resolveSettled = resolve; });
    let done = false;
    const finish = (outcome) => {
      if (done) return;
      done = true;
      if (loadGuard) { clearTimeout(loadGuard); loadGuard = null; }
      if (watchGuard) { clearTimeout(watchGuard); watchGuard = null; }
      resolveSettled({ showed: showingDispatched, ...outcome });
    };

    // Iter62 — IMMEDIATE-FIRE REWARD DISPATCH.
    // Tester report: AdMob test units serve 2 ads back-to-back ("Reward
    // granted" → 2nd ad → "Next ad" button → second "Reward granted").
    // Waiting for Dismissed before invoking onReward dropped rewards
    // because Dismissed sometimes never fires on the test unit and even
    // in production the bridge ordering vs. Rewarded is non-deterministic.
    // Fire the reward callback the MOMENT Google's Rewarded event reaches
    // the JS bridge — idempotent (only the first event dispatches).
    const fireReward = () => {
      if (rewardDispatched) return;
      rewardDispatched = true;
      adDiag(`③ granting reward → calling onReward (${onImmediateReward ? "fn present" : "NO CALLBACK!"})`);
      try {
        Promise.resolve(onImmediateReward?.()).catch((err) => adDiag(`onReward threw: ${err}`));
      } catch (err) { adDiag(`onReward sync-threw: ${err}`); }
    };

    const onRewarded = () => {
      adDiag("② Rewarded EVENT received from AdMob");
      rewarded = true;
      fireReward();
    };
    // Iter87 — CRITICAL reward-loss fix. Rewarded video ads commonly run
    // 15-30 s. The old blanket 15 s timeout below fired WHILE the user
    // was still watching a normal-length ad, tore down the Rewarded
    // listener, and the reward event that arrived at ~30 s was dropped →
    // "watched the ad but energy didn't refill / no coach tip". The
    // `Showed` event tells us the ad is on screen: at that point the
    // LOADING phase is over, so we cancel the load-guard timeout and only
    // keep a generous safety net for the watch phase. We also notify the
    // caller so the UI can stop its own "loading" timeout.
    // Iter90 — ROBUST "ad is showing" transition. We can reach this from
    // EITHER the native `Showed` event OR (more reliably) when the
    // showRewardVideoAd() promise resolves. Whichever fires first: cancel
    // the load guard (loading is over), arm the long watch-phase safety
    // net, and notify the UI. This no longer depends on the `Showed`
    // event reaching the JS bridge — the previous root cause of "ad plays
    // fully but reward never applies" (load guard tore down the reward
    // listener at 12 s, before the ~20 s Rewarded event).
    const markShowing = () => {
      if (showingDispatched) return;
      showingDispatched = true;
      shownAt = Date.now();
      if (loadGuard) { clearTimeout(loadGuard); loadGuard = null; }
      if (!watchGuard) {
        watchGuard = setTimeout(() => { adDiag("✗ WATCH-TIMEOUT 90s"); finish({ rewarded, error: "watch-timeout" }); }, 90000);
      }
      try { onImmediateShowing?.(); } catch { /* swallow */ }
    };
    const onShowed = () => {
      adDiag("① Showed EVENT (ad on screen)");
      markShowing();
    };
    const onDismissed = () => {
      adDiag(`④ Dismissed EVENT (rewarded so far = ${rewarded})`);
      dismissed = true;
      setTimeout(() => {
        const adShownMs = shownAt ? Date.now() - shownAt : 0;
        // Iter73 — STRICT reward policy per tester feedback: only the
        // Rewarded event grants the reward. Closing the ad early (X
        // button) NEVER pays out, even if the user watched ≥ 3s. The
        // earlier permissive 3 s fallback was added for buggy test ads
        // that didn't fire Rewarded, but it also let players cheat by
        // tapping the X button mid-ad and still collecting rewards.
        finish({ rewarded, dismissed: true, adShownMs });
      }, 600);
    };
    const onFailedToLoad = (e) => {
      // Iter91 — `onRewardedVideoAdFailedToLoad` is a GLOBAL event that also
      // fires for BACKGROUND preloads (we re-warm the cache constantly and
      // the production unit chronically returns code 3 / "No fill"). It was
      // aborting the ad the user was actively watching → reward lost. Our
      // OWN load failures are already handled by the awaited
      // prepareRewardVideoAd() promise (see catch below) and by the
      // loadGuard, so this event must NEVER call finish(). Log only.
      adDiag(`(FailedToLoad ${e?.code}/${e?.message} — ignored, non-fatal)`);
    };
    const onFailedToShow = (e) => {
      // A show failure IS about our ad — but if the reward already fired,
      // don't override a successful outcome.
      if (rewardDispatched) { adDiag(`(ignoring FailedToShow ${e?.code} — reward already given)`); return; }
      errorMsg = `FailedToShow: ${e?.code ?? "?"}/${e?.message ?? ""}`;
      adDiag(`✗ ${errorMsg}`);
      finish({ rewarded: false, error: errorMsg });
    };

    // Sequentially AWAIT every addListener so the native plugin has
    // committed each handler before the next line of code runs.
    try {
      subs.push(await AdMob.addListener(RewardAdPluginEvents.Rewarded, onRewarded));
      subs.push(await AdMob.addListener(RewardAdPluginEvents.Showed, onShowed));
      subs.push(await AdMob.addListener(RewardAdPluginEvents.Dismissed, onDismissed));
      subs.push(await AdMob.addListener(RewardAdPluginEvents.FailedToLoad, onFailedToLoad));
      subs.push(await AdMob.addListener(RewardAdPluginEvents.FailedToShow, onFailedToShow));
    } catch (e) {
      // Bridge failure setting up listeners — bail with an error rather
      // than firing a silent ad we can't observe. Remove any listeners
      // that did register before the failure so they don't leak.
      for (const sub of subs) {
        try { sub?.remove?.(); } catch { /* swallow */ }
      }
      return { rewarded: false, error: `addListener threw: ${String(e)}` };
    }

    // Iter87 — LOAD-guard only. This timeout guards the LOADING phase so a
    // wedged prepare/show never hangs the dialog. It is CLEARED the moment
    // the `Showed` event fires (see onShowed) so it can never abort a
    // reward mid-watch. 12 s is ample for a preloaded ad to appear.
    loadGuard = setTimeout(() => { adDiag("✗ LOAD-TIMEOUT 12s (Showed never fired → listeners torn down)"); finish({ rewarded: false, error: "load-timeout" }); }, 12000);

    try {
      const prep = { adId };
      if (userId || rewardType) {
        prep.ssv = {
          userId: String(userId || ""),
          customData: String(rewardType || "coins"),
        };
      }
      // ALWAYS prepare fresh — `preloadedAdId` was a perf optimisation
      // but stale cached creatives caused the "opens for a sec then
      // closes" symptom in iter43. The prepare round-trip is only
      // 1-3s and we've already opened the OOS dialog by the time we
      // get here, so the perceived latency is hidden.
      // Iter61 — Use the cached preload if available. If the ad is NOT
      // pre-loaded, fall through to prepare it here (caller already
      // surfaced "Loading ad…" before invoking us). Setting
      // `preloadedAdId = null` happens AFTER show so the next preload
      // call will fetch a fresh ad.
      // Iter70 — Tester complaint: "ads dont run sometimes" (Polish
      // "Reklama nie załadowała się — spróbuj za chwilę"). Adds an
      // exponential-backoff retry: 0s → 1s → 2.5s before giving up
      // (3 prepare attempts in ~3.5s total). Catches transient network
      // hiccups and AdMob's intermittent "no fill" responses.
      if (preloadedAdId !== adId) {
        // Iter87 — snappier cold-prepare. The old [0,1000,2500] backoff
        // with 6 s timeouts could burn ~9 s before we even fell back to
        // the Google test ID → "ad loading too long". Ads are normally
        // preloaded (Dashboard mount + dialog open), so this cold path is
        // rare; keep it fast: 2 attempts, 4 s ceiling each.
        const delays = [0, 800];
        let lastErr = null;
        let prepared = false;
        for (const delay of delays) {
          if (delay > 0) await new Promise((r) => setTimeout(r, delay));
          try {
            await Promise.race([
              AdMob.prepareRewardVideoAd(prep),
              new Promise((_, reject) =>
                setTimeout(() => reject(new Error("prepare timed out after 4s")), 4000),
              ),
            ]);
            prepared = true;
            break;
          } catch (e) {
            lastErr = e;
          }
        }
        if (!prepared) throw lastErr || new Error("prepare failed after 2 retries");
      }
      preloadedAdId = null;
      shownAt = Date.now();  // iter52 — mark when the fullscreen overlay went up
      // Fire-and-forget show; the listener race above decides outcome.
      adDiag("prepare OK → calling showRewardVideoAd()");
      AdMob.showRewardVideoAd()
        .then(() => {
          // Iter90 — show() resolved → the ad is on screen. Treat this as
          // the authoritative "showing" signal (the native Showed event is
          // unreliable on some devices). This is what keeps the reward
          // listener alive through the full watch.
          adDiag("showRewardVideoAd() resolved → marking showing");
          markShowing();
        })
        .catch((e) => {
          adDiag(`✗ showRewardVideoAd threw: ${String(e)}`);
          if (!errorMsg) errorMsg = `show threw: ${String(e)}`;
        });
    } catch (e) {
      // Prepare failed → no point waiting for events; resolve now.
      const msg = `prepare threw: ${String(e)}`;
      adDiag(`✗ ${msg}`);
      // Iter70 — Schedule a deferred preload retry so the user's NEXT
      // click on "Watch ad" has a much higher chance of working. Most
      // AdMob no-fill errors resolve within 10-30s as new ad inventory
      // becomes available.
      setTimeout(() => {
        preloadRewarded({ userId, rewardType }).catch(() => {});
      }, 8000);
      // Clean up the 4 listeners we registered — otherwise every failed
      // prepare leaks them and they pile up across a session on native.
      for (const sub of subs) {
        try { sub?.remove?.(); } catch { /* swallow */ }
      }
      return { rewarded: false, error: msg };
    }

    const outcome = await settled;
    adDiag(`settled → rewarded=${outcome.rewarded} showed=${outcome.showed} err=${outcome.error || "none"}`);
    // Clean up every listener subscription we registered.
    for (const sub of subs) {
      try { sub?.remove?.(); } catch { /* swallow */ }
    }
    // Iter61 — Tester demand: "After every EARNED_REWARD event fires and
    // the reward action is dispatched, immediately call ad.load() again
    // to pre-load the next ad. Without this, the second ad request
    // always fails with 'ad couldn't load'."
    //
    // We fire-and-forget the preload so the caller's reward dispatch
    // isn't blocked. If the user opens the same dialog seconds later,
    // the ad will already be cached.
    // Iter86 — Keep the ad cache warm after EVERY outcome (not just a
    // successful reward). Previously we only re-preloaded on
    // `outcome.rewarded`, so the 2nd ad in a session — or any ad after a
    // dismiss/timeout — had to fetch fresh, causing the 3-5 s "Loading
    // ad, please wait…" the user complained about. Re-warming on all
    // non-error outcomes means the next ad is already prepared and shows
    // in <1 s. (Hard errors already schedule a deferred preload above.)
    preloadRewarded({ userId, rewardType }).catch(() => {});
    return outcome;
  };

  // 1st attempt — the configured ad unit (real production ID).
  const first = await showWithListeners(pickId("rewarded"), onReward, onShowing);
  if (first.rewarded) return { mocked: false, ...first };

  // Iter87 — if the ad actually SHOWED (user saw it) but we still didn't
  // record a reward, do NOT loop them into a second ad — the reward event
  // may simply be arriving late (Rewarded after Dismissed on some devices)
  // and fireReward is idempotent. A second ad here felt punitive.
  if (first.showed) return { mocked: false, ...first };

  // Fall back to the universal Google test ID on ANY failure (no fill,
  // network error, FailedToLoad, FailedToShow, or simply dismissed).
  // For "user dismissed early" we still try once with the test ID —
  // some users dismiss because the production ad never started playing.
  if (first.dismissed && !first.error) {
    // User explicitly dismissed a working ad. Don't loop them through
    // a second ad — just return without reward.
    return { mocked: false, ...first };
  }
  // eslint-disable-next-line no-console
  console.warn("rewarded production ID failed (", first.error, ") — retrying with Google test ID");
  const testId = TEST_IDS.rewarded[platform() === "ios" ? "ios" : "android"];
  if (pickId("rewarded") === testId) return { mocked: false, ...first };
  const fallback = await showWithListeners(testId, onReward, onShowing);
  return { mocked: false, ...fallback, fellBackToTestId: true };
}

export async function showBanner() {
  const ok = await ensureInit();
  if (!ok) return { mocked: true };
  try {
    await AdMob.showBanner({
      adId: pickId("banner"),
      adSize: BannerAdSize.ADAPTIVE_BANNER,
      position: BannerAdPosition.BOTTOM_CENTER,
      margin: 0,
    });
    return { mocked: false };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("banner show failed", e);
    return { mocked: false, error: String(e) };
  }
}

export async function hideBanner() {
  if (!isNative()) return;
  try { await AdMob?.hideBanner?.(); } catch {}
}

/**
 * Pre-load a rewarded ad in the background so the user doesn't sit through
 * a 2-5s "Loading…" spinner when they tap Watch ad. Call this on Dashboard
 * mount and again any time we know the user is about to be offered an ad
 * (e.g., when the Energy refill dialog opens, after the user has just
 * burned a life). No-op on web. Idempotent.
 */
export async function preloadRewarded({ userId, rewardType } = {}) {
  if (!isNative()) return;
  const ok = await ensureInit();
  if (!ok) return;
  const adId = pickId("rewarded");
  if (preloadedAdId === adId) return;  // already cached
  try {
    const prep = { adId };
    if (userId || rewardType) {
      prep.ssv = {
        userId: String(userId || ""),
        customData: String(rewardType || "coins"),
      };
    }
    await AdMob.prepareRewardVideoAd(prep);
    preloadedAdId = adId;
  } catch {
    preloadedAdId = null;
  }
}
